import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { DownloadSimple, SealCheck, Warning } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { istDateKey } from '@/lib/ist'
import { Badge, Banner, Button, DataTable, EmptyState, FilterChip, type Column, type Tone } from '@/ui'
import { errorMessage } from '../api/core'
import {
  DECLARATION_STATE_LABEL,
  fetchAllBayDeclarations,
  useBayDeclarations,
  useDeclarationForm,
  useOutOfDateBays,
  useSites,
  type BayDeclarationRow,
  type OutOfDateBay,
} from '../api/store'
import { DetailDrawer } from '../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage, QueryState } from '../components/Layout'
import { PhotoViewer } from '../components/PhotoViewer'
import { Bi, Check, Code, Facts, Section, UserName, When } from '../components/bits'
import { downloadText, toCsv } from '../lib/csv'
import { useUrlState } from '../lib/useUrlState'
import { declarationAnswerRows, declarationsCsv } from '../model/declarationForm'

const LIMIT = 50
const STATE_TONE: Record<string, Tone> = { NONE: 'neutral', EXPIRED_OR_OLD_FORM: 'danger', NOT_CLEAN: 'danger', EXPIRING: 'warn' }

/** Declarations (F5.4-b/f/g): bays that need a no-fumigant declaration, and every one signed. */
export function DeclarationsPage() {
  const [f, setF, { reset, active }] = useUrlState({ tab: 'due', site: '', bay: '', page: 1, open: '' })
  const sites = useSites()
  const siteOptions = (sites.data ?? [])
    .filter((s) => s.kind !== 'COLLECTION_POINT')
    .map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))
  const due = useOutOfDateBays(f.site || undefined)
  const dueCount = due.data?.length ?? 0

  const filters = (
    <FilterBar onClear={reset} active={active && Boolean(f.site || f.bay)}>
      <SelectFilter label="Site" value={f.site} onChange={(site) => setF({ site, bay: '' })} options={siteOptions} allLabel="All sites" />
      {f.bay && (
        <div className="flex h-9 items-center">
          <FilterChip active onClick={() => setF({ bay: '' })}>
            One bay · show all
          </FilterChip>
        </div>
      )}
    </FilterBar>
  )

  return (
    <ConsolePage
      eyebrow="Store"
      title="Declarations"
      description="The keeper signs a no-fumigant declaration for each bay on the phone. Brand produce in a bay without one in force goes on hold."
    >
      <div role="tablist" aria-label="Declarations" className="flex gap-1 border-b border-rule">
        <Tab selected={f.tab !== 'all'} onClick={() => setF({ tab: 'due', bay: '' })}>
          Bays needing a declaration
          {dueCount > 0 && <Badge tone="warn" className="ml-2">{dueCount}</Badge>}
        </Tab>
        <Tab selected={f.tab === 'all'} onClick={() => setF({ tab: 'all' })}>
          All declarations
        </Tab>
      </div>
      {f.tab === 'all' ? (
        <AllDeclarations
          siteId={f.site}
          bayId={f.bay}
          page={f.page}
          onPage={(page) => setF({ page })}
          openId={f.open}
          onOpen={(open) => setF({ open })}
          filters={filters}
        />
      ) : (
        <DueBays q={due} filters={filters} onHistory={(bay) => setF({ tab: 'all', bay })} />
      )}
    </ConsolePage>
  )
}

function Tab({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={cn(
        '-mb-px inline-flex items-center border-b-2 px-3 py-2 text-[14px] font-semibold transition-colors',
        selected ? 'border-amber text-ink' : 'border-transparent text-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  )
}

function DueBays({
  q,
  filters,
  onHistory,
}: {
  q: ReturnType<typeof useOutOfDateBays>
  filters: ReactNode
  onHistory: (bayId: string) => void
}) {
  const rows = q.data ?? []
  const atRisk = rows.filter((r) => r.brandBags > 0 && r.state !== 'EXPIRING')
  const columns: Column<OutOfDateBay>[] = [
    {
      id: 'bay',
      header: 'Bay',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="text-ink">{r.path}</span>
          <Code className="text-muted">{r.bayCode}</Code>
        </span>
      ),
      sortValue: (r) => r.path,
    },
    { id: 'state', header: 'State', cell: (r) => <Badge tone={STATE_TONE[r.state] ?? 'neutral'}>{DECLARATION_STATE_LABEL[r.state] ?? r.state}</Badge>, sortValue: (r) => r.state },
    {
      id: 'bags',
      header: 'Brand bags in it',
      align: 'right',
      mono: true,
      cell: (r) =>
        r.brandBags > 0 ? (
          <span className={cn('inline-flex items-center gap-1 font-bold', r.state === 'EXPIRING' ? 'text-warn' : 'text-danger')}>
            <Warning aria-hidden="true" />
            {r.brandBags}
          </span>
        ) : (
          <span className="text-muted">0</span>
        ),
      sortValue: (r) => r.brandBags,
    },
    { id: 'valid', header: 'Valid to', cell: (r) => <When at={r.validTo} dateOnly />, sortValue: (r) => r.validTo, hideBelow: 'sm' },
    {
      id: 'links',
      header: <span className="sr-only">Links</span>,
      cell: (r) => (
        <span className="flex justify-end gap-3 text-[13px]">
          {r.state !== 'NONE' && (
            <button type="button" className="underline decoration-rule underline-offset-2 hover:decoration-amber" onClick={() => onHistory(r.bayId)}>
              History
            </button>
          )}
          <Link
            to={`/c/locations?site=${r.siteId ?? ''}&place=${r.bayId}`}
            className="underline decoration-rule underline-offset-2 hover:decoration-amber"
          >
            Open bay
          </Link>
        </span>
      ),
      hideBelow: 'md',
    },
  ]
  return (
    <>
      {atRisk.length > 0 && (
        <Banner tone="error" title={`${atRisk.reduce((n, r) => n + r.brandBags, 0)} brand bags sit in ${atRisk.length} bay${atRisk.length === 1 ? '' : 's'} without a clean declaration`}>
          They are held until the keeper signs a declaration for the bay, or the bags are moved to a declared bay.
        </Banner>
      )}
      <DataTable<OutOfDateBay>
        caption="Bays needing a declaration"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.bayId}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        initialSort={{ id: 'bags', dir: 'desc' }}
        pageSize={50}
        filters={filters}
        searchText={(r) => `${r.path} ${r.bayCode}`}
        searchPlaceholder="Bay or code"
        empty={<EmptyState icon={<SealCheck />} title="Every bay is declared" description="No bay is missing, past or close to the end of its declaration." />}
      />
    </>
  )
}

function AllDeclarations({
  siteId,
  bayId,
  page,
  onPage,
  openId,
  onOpen,
  filters,
}: {
  siteId: string
  bayId: string
  page: number
  onPage: (p: number) => void
  openId: string
  onOpen: (id: string) => void
  filters: ReactNode
}) {
  const scope = { siteId: siteId || undefined, locationId: bayId || undefined }
  const q = useBayDeclarations({ ...scope, page, limit: LIMIT })
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const rows = q.data?.rows ?? []
  const open = rows.find((r) => r.id === openId) ?? null

  const exportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const all = await fetchAllBayDeclarations(scope)
      const csv = declarationsCsv(all)
      downloadText(`bay-declarations-${istDateKey(new Date())}.csv`, toCsv(csv.header, csv.rows))
    } catch (e) {
      setExportError(errorMessage(e))
    } finally {
      setExporting(false)
    }
  }

  const columns: Column<BayDeclarationRow>[] = [
    {
      id: 'bay',
      header: 'Bay',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="text-ink">{r.bayPath}</span>
          <Code className="text-muted">{r.bayCode}</Code>
        </span>
      ),
      sortValue: (r) => r.bayPath,
    },
    { id: 'by', header: 'Signed by', cell: (r) => r.signedByName ?? <UserName id={r.signedById} />, hideBelow: 'md' },
    { id: 'at', header: 'Signed', cell: (r) => <When at={r.signedAt} />, sortValue: (r) => r.signedAt },
    { id: 'valid', header: 'Valid to', cell: (r) => <When at={r.validTo} dateOnly />, sortValue: (r) => r.validTo, hideBelow: 'sm' },
    { id: 'clean', header: 'Clean', cell: (r) => <Check ok={r.clean} label={r.clean === null ? '—' : r.clean ? 'Yes' : 'No'} /> },
    {
      id: 'force',
      header: 'In force',
      cell: (r) => (r.inForce ? <Badge tone="ok">In force</Badge> : r.latest ? <Badge tone="danger">Lapsed</Badge> : <Badge>Replaced</Badge>),
    },
    {
      id: 'photo',
      header: 'Photo',
      cell: (r) => (r.photoArrived ? <Check ok label="Arrived" /> : <span className="text-[13px] text-warn">Not uploaded yet</span>),
      hideBelow: 'lg',
    },
  ]
  return (
    <>
      {exportError && <Banner tone="error" title="Export failed">{exportError}</Banner>}
      <DataTable<BayDeclarationRow>
        caption="Bay declarations"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => onOpen(r.id)}
        pageSize={LIMIT}
        server={{ page, total: q.data?.total ?? 0, onPageChange: onPage }}
        filters={
          <div className="flex w-full flex-wrap items-end justify-between gap-2">
            {filters}
            <Button size="sm" variant="secondary" leadingIcon={<DownloadSimple />} loading={exporting} disabled={!q.data?.total} onClick={() => void exportCsv()}>
              Export CSV
            </Button>
          </div>
        }
        empty={<EmptyState icon={<SealCheck />} title="No declarations" description="Declarations appear here once a keeper signs one for a bay on the phone." />}
      />
      {open && <DeclarationDrawer d={open} onClose={() => onOpen('')} />}
    </>
  )
}

/** The signed form: each question next to the answer given, the signer, the dates and the photo. */
function DeclarationDrawer({ d, onClose }: { d: BayDeclarationRow; onClose: () => void }) {
  const form = useDeclarationForm(d.formCode)
  const answers = declarationAnswerRows(form.data ?? null, d.answers)
  const otherVersion = form.data && form.data.version !== d.formVersion
  return (
    <DetailDrawer
      open
      onClose={onClose}
      eyebrow="No-fumigant declaration"
      title={d.bayPath}
      meta={
        <>
          <Code>{d.bayCode}</Code>
          {d.clean === false && <Badge tone="danger">Not clean</Badge>}
          {d.inForce ? <Badge tone="ok">In force</Badge> : <Badge>{d.latest ? 'Lapsed' : 'Replaced'}</Badge>}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Facts
          columns={2}
          items={[
            { label: 'Signed by', value: d.signedByName ?? <UserName id={d.signedById} /> },
            { label: 'Signed', value: <When at={d.signedAt} /> },
            { label: 'Valid to', value: <When at={d.validTo} dateOnly /> },
            { label: 'Clean', value: <Check ok={d.clean} label={d.clean === null ? '—' : d.clean ? 'Yes' : 'No'} /> },
          ]}
        />
        <Section title="Answers" eyebrow={`Form version ${d.formVersion}`}>
          <QueryState query={form} label="the form">
            {otherVersion && (
              <p className="mb-3 text-[13px] text-muted">
                Signed on version {d.formVersion}; the questions shown are from the current version {form.data?.version}.
              </p>
            )}
            <ol className="divide-y divide-rule/60">
              {answers.map((a) => (
                <li key={a.key} className="flex items-start justify-between gap-4 py-2.5">
                  <Bi en={a.question} hi={a.questionHi} className="text-[14px] text-ink" />
                  <span
                    className={cn(
                      'shrink-0 font-mono text-[14px] font-bold',
                      a.risky && 'rounded-chip bg-danger-wash px-1.5 text-danger',
                      a.missing && 'text-warn',
                    )}
                  >
                    {a.missing ? 'Not answered' : a.answer}
                  </span>
                </li>
              ))}
            </ol>
          </QueryState>
        </Section>
        <Section title="Photo" eyebrow={d.photoArrived ? 'Taken when signing' : 'Not uploaded from the phone yet'}>
          <PhotoViewer photo={d.photo} alt={`Declaration photo · ${d.bayPath}`} size="inline" />
        </Section>
      </div>
    </DetailDrawer>
  )
}
